import React, {
  forwardRef,
  ForwardRefExoticComponent,
  useEffect,
  useRef,
  useState,
} from "react";
import Avatar from "../Avatar";
import "./DropdownMenu.scss";
import {
  DropDownItemGroupProps,
  DropDownItemProps,
  DropDownMenuProps,
  GroupItem,
  SubDropDownProps,
} from "./DropdownMenu.types";
import Tippy from "@tippyjs/react/headless";
import { motion, useSpring } from "framer-motion";
import { dropdown } from "../../animations/dropdown";
import { CheckBox } from "../Checkbox/Checkbox";
import { useHover } from "../../hooks/useHover";

export const DropDownGroup: React.FC<DropDownItemGroupProps> = ({
  handleClick,
  maxHeight,
  groupItems,
  dropDownItemClassName,
  clickType,
  handleMouseOut,
  renderLocation,
  itemId,
  itemValue,
  isVisible = false,
  isSubDropDown = false,
  hasSubDropDown = false,
}) => {
  const colorPickerStyles =
    clickType === "colorPicker"
      ? "alteon-dropdown-color-picker-trigger"
      : "alteon-dropdown-trigger-default";
  const rootStyles: any = { maxHeight };
  if (!hasSubDropDown) {
    rootStyles.overflow = 'hidden auto';
  }

  return (
    <div
      className={`${colorPickerStyles} ${isSubDropDown ? 'alteon-dropdown-sub-dropdown' : ''}`}
      style={rootStyles}
      onMouseLeave={() => {
        handleMouseOut;
      }}
    >
      {groupItems &&
        groupItems.map((el) => {
          return (
            <DropDownItem
              renderLocation={renderLocation}
              className={`${dropDownItemClassName} ${el.className}`}
              key={el.id ? el.id : el.label}
              itemId={itemId}
              itemValue={itemValue}
              iconVariant={el.iconVariant}
              iconDirection={el.iconDirection}
              checked={el.checked}
              onChange={el.onChange}
              checkBoxDataTestId={el.checkBoxDataTestId}
              clickType={clickType}
              onClick={() => {
                if (clickType === "multiselect") {
                  el.onClick;
                } else {
                  handleClick(el.onClick);
                }
              }}
              handleClick={handleClick}
              iconPath={el.iconPath}
              variant={el.type}
              subGroupItems={el.subGroupItems}
              isVisible={isVisible}
              title={el.label}
              dataTestIdForItem={el.dataTestIdForItem}
            >
              {clickType !== "colorPicker" && el.label}
            </DropDownItem>
          );
        })}
    </div>
  );
};

export const DropDownItem: React.FC<DropDownItemProps> = ({
  className,
  disabled,
  dataTestIdForItem,
  iconVariant,
  avatarPath,
  iconDirection,
  onClick,
  iconPath,
  variant,
  children,
  title,
  checked,
  onChange,
  handleClick,
  checkBoxDataTestId,
  clickType,
  renderLocation,
  subGroupItems = [],
  itemId,
  itemValue,
  isVisible= false,
  ...props
}) => {
  const [showSubGroupItems, setShowSubGroupItems] = useState(false)
  const classes = variant
    ? `alteon-dropdown-item-variant ${className}`
    : `alteon-dropdown-item ${className}`;
  const notification =
    variant === "notification" || disabled === true
      ? "alteon-dropdown-item-notification"
      : "alteon-dropdown-item";
  const renderRight = iconPath && iconDirection === "right" ? true : false;
  const avatarClass =
    iconVariant === "avatar" ? `alteon-dropdown-item-avatar-span` : "";

  const directionalClasses =
    renderRight === true
      ? `${classes} alteon-dropdown-item-right`
      : `${classes}`;

  const multiSelectClasses =
    clickType === "multiselect"
      ? `alteon-dropdown-item-multi-select ${className}`
      : `${classes}`;

  const variantClasses =
    variant === "notifications" && renderRight === false
      ? notification
      : directionalClasses;

  const dropDownItemClassName =
    clickType === "multiselect" ? multiSelectClasses : variantClasses;

  const colorPickerDropDownClass =
    clickType === "colorPicker"
      ? "alteon-dropdown-menu-color-items"
      : "alteon-dropdown-menu-items";

  useEffect(() => {
    if (!isVisible) {
      setShowSubGroupItems(false);
    }
  }, [isVisible]);

  return (
    <div
      className={dropDownItemClassName}
      {...props}
      onClick={() => {
        if (clickType === "multiselect") {
          return;
        } else if (subGroupItems.length) {
          setShowSubGroupItems(!showSubGroupItems)
        } else if (clickType === "colorPicker") {
          onChange({
            value: itemValue,
            id: itemId,
            color: title,
            location: renderLocation,
          });
          onClick();
        } else {
          onClick();
        }
      }}
      title={title}
      data-testid={dataTestIdForItem}
    >
      {/* Rather than check the icon paths, we should add param
        to check if it's a multi select.

        In that case, we don't render the icons/ avatar stuff. 
        We just render the Checkbox with the passe din params 
      */}
      {clickType === "multiselect" && (
        <CheckBox
          checked={checked}
          onChange={onChange}
          checkBoxDataTestId={checkBoxDataTestId}
          name={title}
          className={`alteon-dropdown-checkbox`}
          dataTestId={checkBoxDataTestId}
        />
      )}
      {iconPath && iconDirection === "left" && (
        <div className={colorPickerDropDownClass}>
          {iconVariant !== "avatar" && typeof iconPath === "string" && (
            <img src={iconPath} />
          )}
          {iconVariant !== "avatar" && typeof iconPath !== "string" && iconPath}
        </div>
      )}
      {iconVariant === "avatar" && (
        <div
          style={{
            flexDirection: "row",
            marginRight: "1em",
          }}
        >
          <Avatar shape="circle" color="black" src={avatarPath} />
        </div>
      )}
      <span className={avatarClass}>{children}</span>

      {showSubGroupItems && subGroupItems.length &&
        <DropDownGroup groupItems={subGroupItems} isSubDropDown handleClick={handleClick} isVisible={isVisible} />}

      {renderRight && (
        <div
          style={{
            flexDirection: "row",
            marginLeft: "1em",
          }}
        >
          {typeof iconPath === "string" ? <img src={iconPath} /> : iconPath}
        </div>
      )}
    </div>
  );
};

const filterGroups = (groupItems: GroupItem[]) => {
  return [
    ...groupItems.filter((t) => t.pinned !== true),
    ...groupItems.filter((t) => t.pinned === true),
  ];
};

const getHasSubDropDown = (groupItems: GroupItem[] = []) => {
  return groupItems.some(({ groupItems }) => groupItems && groupItems.some(item => !!item.subGroupItems));
};

export const DropdownMenu: ForwardRefExoticComponent<
  DropDownMenuProps & React.RefAttributes<HTMLDivElement>
> = forwardRef((props, ref) => {
  const {
    itemValue,
    itemId,
    triggerClass,
    trigger,
    listItems,
    overflow,
    onOpen = () => false,
    onClose = () => false,
    placement = "bottom",
    type,
    maxWidth,
    className,
    dropDownItemClassName,
    appendTo,
    children,
    emptyNotificationsLabel,
    emptyNotificationsLabelClass,
    triggerType = "click",
    folderTree,
    disabled,
    hideDropdown = false,
    customDropdownContainerClass,
    renderLocation,
    dataTestid,
    dataTestIdForItem
  } = props;
  const [currentLocation, setCurrentLocation] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [tippyInstance, setTippyInstance] = useState(null);
  const [duration, setDuration] = useState(300);
  const [overflowClasses, setOverflowClasses] = useState('');
  const list = useRef(null);
  const springConfig = { damping: 40, duration, stiffness: 200 };
  const initialHeight = 0;
  const height = useSpring(initialHeight, springConfig);
  const hasSubDropDown = getHasSubDropDown(listItems);

  const onMount = () => {
    setDuration(300);
    onOpen();
    height.set(list.current.clientHeight);
  };
  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (hideDropdown) {
      setIsVisible(false);
      if (tippyInstance) {
        tippyInstance.hide();
      }
    }
  }, [hideDropdown, tippyInstance, tippyInstance]);

  useEffect(() => {
    if (renderLocation) {
      setCurrentLocation(renderLocation);
    }
  }, [renderLocation]);

  useEffect(() => {
    if (!hasSubDropDown) {
      return;
    }
    if (isVisible) {
      setTimeout(() => {
        setOverflowClasses('alteon-dropdown-allow-overflow');
      }, 300);
    } else {
      setOverflowClasses('');
    }
  }, [hasSubDropDown, isVisible]);

  const [hoverRef, hovered] = useHover();
  const onHide = (instance) => {
    const cleanup = height.onChange((value) => {
      if (value <= initialHeight) {
        cleanup();

        if (instance) {
          instance.unmount();
          onClose();
        }
      }
    });

    height.set(initialHeight);
  };

  const handleClick = (onClick) => {
    setDuration(100);
    setIsVisible(false);
    if (tippyInstance) {
      tippyInstance.hide();
    }
    if (onClick) {
      onClick();
    }
  };

  useEffect(() => {
    if (hovered && !isVisible) {
      setIsVisible(!isVisible);
    }
  }, [hovered, isVisible]);

  const handleMouseOut = () => {
    setIsVisible(false);
  };

  const classes = `alteon-dropdown-tippy-menu alteon-dropdown-tippy-menu-overflow ${overflow} ${overflowClasses}`;
  const dropDownContainerClass = `alteon-dropdown-container rounded ${location} ${className}`;
  const filteredGroups = listItems && filterGroups(listItems);
  const groupBorderClass = `alteon-dropdown-group-item`;
  const folderTreeStyle = folderTree ? "alteon-folder-dropdown-class" : "";
  return (
    <Tippy
      render={(attrs) => {
        return (
          <motion.div
            className={`alteon-dropdown-tippy-root ${dropDownContainerClass}`}
            initial="collapsed"
            animate={isVisible ? "open" : "collapsed"}
            exit="collapsed"
            {...attrs}
            ref={hoverRef as any}
            data-testid={dataTestid}
          >
            <div style={{ maxWidth: maxWidth }}>
              <motion.div
                variants={placement !== "right" && dropdown}
                className={`${classes} ${folderTreeStyle}`}
              >
                <div className={customDropdownContainerClass} ref={list}>
                  {(type === "dropdown" || type === "multiselect") &&
                    folderTree &&
                    folderTree.slice(0).map((item, index) => {
                      return (
                        <div
                          key={`directory-${index}`}
                          className="alteon-dropdown-file-tree-items-parent"
                        >
                          <button
                            style={{
                              all: "unset",
                              paddingLeft: 10 * index,
                              width: "100%",
                            }}
                            onClick={item.onClick}
                          >
                            <div className={"alteon-dropdown-folder-item"}>
                              {item.icon && typeof item.icon === "string" ? (
                                <img
                                  className={"alteon-dropdown-file-tree-icon"}
                                  src={item.icon}
                                />
                              ) : (
                                item.icon
                              )}

                              <div
                                className={"alteon-dropdown-file-tree-items"}
                              >
                                {item.label}
                              </div>
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  {type === "notifications" && (
                    <div>
                      {React.Children.count(children) > 0 &&
                      type === "notifications" ? (
                        children
                      ) : (
                        <div
                          className={`alteon-dropdown-empty-notifications-label ${emptyNotificationsLabelClass}`}
                        >
                          {emptyNotificationsLabel}
                        </div>
                      )}
                    </div>
                  )}
                  {(type === "dropdown" ||
                    type === "multiselect" ||
                    type === "colorPicker") &&
                    !folderTree &&
                    filteredGroups.map((item) => {
                      if (item !== filteredGroups[filteredGroups.length - 1]) {
                        return (
                          <div>
                            <DropDownGroup
                              itemValue={itemValue}
                              itemId={itemId}
                              renderLocation={renderLocation}
                              key={item.key}
                              maxHeight={item.maxHeight}
                              groupItems={item.groupItems}
                              dropDownItemClassName={dropDownItemClassName}
                              handleClick={handleClick}
                              clickType={type}
                              handleMouseOut={handleMouseOut}
                              hasSubDropDown={hasSubDropDown}
                              isVisible={isVisible}
                              dataTestIdForItem={dataTestIdForItem}
                            />
                            <hr className={`${groupBorderClass}`} />
                          </div>
                        );
                      } else if (
                        item === filteredGroups[filteredGroups.length - 1]
                      ) {
                        return (
                          <div>
                            <DropDownGroup
                              itemValue={itemValue}
                              itemId={itemId}
                              key={item.key}
                              maxHeight={item.maxHeight}
                              groupItems={item.groupItems}
                              dropDownItemClassName={dropDownItemClassName}
                              handleClick={handleClick}
                              renderLocation={currentLocation}
                              clickType={type}
                              hasSubDropDown={hasSubDropDown}
                              isVisible={isVisible}
                              dataTestIdForItem={dataTestIdForItem}
                            />
                          </div>
                        );
                      }
                    })}
                </div>
              </motion.div>
            </div>
          </motion.div>
        );
      }}
      animation={"true"}
      disabled={disabled}
      arrow={true}
      theme="light-border"
      trigger={triggerType === "hover" ? "mouseenter" : "click"}
      interactive={true}
      placement={placement ? placement : "bottom"}
      appendTo={appendTo === "document" ? () => document.body : "parent"}
      onHide={onHide}
      onMount={onMount}
      onTrigger={() => {
        setIsVisible(true);
      }}
      onUntrigger={() => setIsVisible(false)}
      onCreate={setTippyInstance}
      onClickOutside={() => setIsVisible(false)}
      ref={ref}
    >
      <div
        className={triggerClass}
        style={{ display: "inline-block", cursor: "pointer" }}
      >
        {trigger}
      </div>
    </Tippy>
  );
});

export const SubDropDown: React.FC<SubDropDownProps> = ({
  label,
  children,
  trigger,
  labelIcon,
  ...props
}) => {
  const [isActive, setIsActive] = useState(false);
  const dropdownClass = isActive
    ? "alteon-sub-dropdown active"
    : "alteon-sub-dropdown";

  let dropDownLabelEventHandlers = {};
  if (trigger === "click") {
    dropDownLabelEventHandlers = {
      onClick: () => setIsActive(!isActive),
    };
  } else {
    dropDownLabelEventHandlers = {
      onMouseEnter: () => setIsActive(true),
      onMouseLeave: () => setIsActive(false),
    };
  }
  return (
    <div
      className="alteon-sub-dropdown-container"
      {...props}
      {...dropDownLabelEventHandlers}
    >
      <p className="alteon-dropdown-label">
        {label}
        {labelIcon}
      </p>
      <div className={dropdownClass}>{children}</div>
    </div>
  );
};

SubDropDown.defaultProps = {
  label: "click me",
};
